import { i as isKeyboardUser } from "./focus-D9T8n_CN.js";
class HeaderMenuV2 extends HTMLElement {
  constructor() {
    super();
    this.config = {
      triggerType: this.dataset.triggerType || "hover",
      // 'hover' | 'click'
      hoverDelay: 150,
      // Delay before closing dropdown on mouse leave
      mobileBreakpoint: 991
    };
    this._activeDropdowns = /* @__PURE__ */ new Set();
    this._hoverTimeout = null;
    this._hoverTimeoutTarget = null;
    this._boundHandlers = {};
    this._activeMegamenu = null;
    this._megamenuHoverTimeout = null;
    this._teleportedMegamenus = [];
    this._scrollObserver = null;
    this._scrollDebounce = null;
    this._activeMobileDrawer = null;
    this._activeMobilePanels = /* @__PURE__ */ new Set();
    this._mobileDrawerObserver = null;
    this._mobileNavLevel = 0;
    this._mobileTriggers = /* @__PURE__ */ new Map();
    this._wasMobile = null;
    this._resizeTimeout = null;
  }
  connectedCallback() {
    this.init();
  }
  init() {
    this._searchRoot = this.closest("header-v3-component") || this;
    if (this._searchRoot._headerMenuSetup) {
      this.classList.add("is-initialized");
      return;
    }
    this._searchRoot._headerMenuSetup = true;
    this._megamenuContainer = this._findMegamenuContainer();
    this._boundHandlers = {
      handleDocumentClick: this._handleDocumentClick.bind(this),
      handleKeydown: this._handleKeydown.bind(this),
      handleFocusOut: this._handleFocusOut.bind(this),
      handleMouseOver: this._handleMouseOverDelegated.bind(this),
      handleMouseOut: this._handleMouseOutDelegated.bind(this),
      handleFocusIn: this._handleFocusInDelegated.bind(this),
      handleFocusOutDelegated: this._handleFocusOutDelegated.bind(this),
      handleContainerMouseOver: this._handleContainerMouseOver.bind(this),
      handleContainerMouseOut: this._handleContainerMouseOut.bind(this),
      handleClick: this._handleClickDelegated.bind(this)
    };
    this._desktopEventsBound = false;
    this._wasMobile = window.innerWidth <= this.config.mobileBreakpoint;
    this._updateDesktopDelegation();
    if (this._megamenuContainer) {
      this._megamenuContainer.querySelectorAll("[data-mega-id]").forEach((panel) => {
        panel.inert = true;
      });
    }
    this._setupMobileNavigation();
    this._setupMobileDrawerObserver();
    document.addEventListener("click", this._boundHandlers.handleDocumentClick);
    document.addEventListener("keydown", this._boundHandlers.handleKeydown);
    this._boundHandlers.handleCloseAll = this._closeAllDropdowns.bind(this);
    this.addEventListener("close-all-dropdowns", this._boundHandlers.handleCloseAll);
    this._setupResizeHandler();
    if (this._isMobile()) {
      this._teleportMegamenus();
    }
    this.classList.add("is-initialized");
  }
  // ─── DOM Queries ─────────────────────────────────────────────────────────────
  _findMegamenuContainer() {
    const v3Component = this.closest("header-v3-component");
    if (v3Component) {
      const inner = v3Component.querySelector(".header-mega-container");
      if (inner) return inner;
    }
    return null;
  }
  /**
   * Get the megamenu-mobile-container for a given megamenu target ID.
   * The container is the lvl-2 dropdown wrapper that slides in/out on mobile.
   */
  _getMegamenuMobileContainer(targetId) {
    var _a;
    return (_a = this._searchRoot.querySelector(
      `.megamenu-mobile-container .megamenu-mobile-slot[data-mega-slot="${targetId}"]`
    )) == null ? void 0 : _a.closest(".megamenu-mobile-container");
  }
  // ─── Desktop Event Delegation ────────────────────────────────────────────────
  _updateDesktopDelegation() {
    if (!this._searchRoot || this._searchRoot._headerMenuSetup !== true) return;
    const isMobile = this._isMobile();
    if (isMobile && this._desktopEventsBound) {
      this._searchRoot.removeEventListener("mouseover", this._boundHandlers.handleMouseOver);
      this._searchRoot.removeEventListener("mouseout", this._boundHandlers.handleMouseOut);
      this._searchRoot.removeEventListener("click", this._boundHandlers.handleClick);
      this._searchRoot.removeEventListener("focusin", this._boundHandlers.handleFocusIn);
      this._searchRoot.removeEventListener("focusout", this._boundHandlers.handleFocusOut);
      this._searchRoot.removeEventListener("focusout", this._boundHandlers.handleFocusOutDelegated);
      if (this._megamenuContainer) {
        this._megamenuContainer.removeEventListener("mouseover", this._boundHandlers.handleContainerMouseOver);
        this._megamenuContainer.removeEventListener("mouseout", this._boundHandlers.handleContainerMouseOut);
      }
      this._desktopEventsBound = false;
    } else if (!isMobile && !this._desktopEventsBound) {
      if (this.config.triggerType === "click") {
        this._searchRoot.addEventListener("click", this._boundHandlers.handleClick);
      } else {
        this._searchRoot.addEventListener("mouseover", this._boundHandlers.handleMouseOver);
        this._searchRoot.addEventListener("mouseout", this._boundHandlers.handleMouseOut);
        this._searchRoot.addEventListener("focusin", this._boundHandlers.handleFocusIn);
        this._searchRoot.addEventListener("focusout", this._boundHandlers.handleFocusOut);
        this._searchRoot.addEventListener("focusout", this._boundHandlers.handleFocusOutDelegated);
        if (this._megamenuContainer) {
          this._megamenuContainer.addEventListener("mouseover", this._boundHandlers.handleContainerMouseOver);
          this._megamenuContainer.addEventListener("mouseout", this._boundHandlers.handleContainerMouseOut);
        }
      }
      this._desktopEventsBound = true;
    } else if (!isMobile && this._desktopEventsBound) {
      this._searchRoot.removeEventListener("mouseover", this._boundHandlers.handleMouseOver);
      this._searchRoot.removeEventListener("mouseout", this._boundHandlers.handleMouseOut);
      this._searchRoot.removeEventListener("click", this._boundHandlers.handleClick);
      this._searchRoot.removeEventListener("focusin", this._boundHandlers.handleFocusIn);
      this._searchRoot.removeEventListener("focusout", this._boundHandlers.handleFocusOut);
      this._searchRoot.removeEventListener("focusout", this._boundHandlers.handleFocusOutDelegated);
      if (this._megamenuContainer) {
        this._megamenuContainer.removeEventListener("mouseover", this._boundHandlers.handleContainerMouseOver);
        this._megamenuContainer.removeEventListener("mouseout", this._boundHandlers.handleContainerMouseOut);
      }
      if (this.config.triggerType === "click") {
        this._searchRoot.addEventListener("click", this._boundHandlers.handleClick);
      } else {
        this._searchRoot.addEventListener("mouseover", this._boundHandlers.handleMouseOver);
        this._searchRoot.addEventListener("mouseout", this._boundHandlers.handleMouseOut);
        this._searchRoot.addEventListener("focusin", this._boundHandlers.handleFocusIn);
        this._searchRoot.addEventListener("focusout", this._boundHandlers.handleFocusOut);
        this._searchRoot.addEventListener("focusout", this._boundHandlers.handleFocusOutDelegated);
        if (this._megamenuContainer) {
          this._megamenuContainer.addEventListener("mouseover", this._boundHandlers.handleContainerMouseOver);
          this._megamenuContainer.addEventListener("mouseout", this._boundHandlers.handleContainerMouseOut);
        }
      }
    }
  }
  // ─── Desktop Mouse Handlers ──────────────────────────────────────────────────
  _handleMouseOverDelegated(e) {
    const link = e.target.closest(".menu-header-v3-item-link, .menu-header-v3-subitem-link");
    if (!link) return;
    const item = link.closest(".menu-header-v3-item.has-dropdown, .menu-header-v3-subitem.has-dropdown");
    if (item) {
      if (item.classList.contains("has-megamenu")) {
        this._openMegamenu(item);
      } else {
        this._openDropdown(item);
      }
    }
  }
  _handleFocusInDelegated(e) {
    if (this._isMobile()) return;
    const item = e.target.closest(".menu-header-v3-item.has-dropdown, .menu-header-v3-subitem.has-dropdown");
    if (item) {
      if (item.classList.contains("has-megamenu")) {
        this._openMegamenu(item);
      } else {
        this._openDropdown(item);
      }
    }
  }
  _handleFocusOutDelegated(e) {
    if (this._isMobile()) return;
    setTimeout(() => {
      const active = document.activeElement;
      if (!active) return;
      if (this._megamenuContainer && this._megamenuContainer.contains(active)) {
        return;
      }
      const activeItem = this._searchRoot.querySelector(".menu-header-v3-item.is-megamenu-active, .menu-header-v3-item.is-dropdown-active");
      if (activeItem && activeItem.contains(active)) {
        return;
      }
      if (activeItem) {
        if (activeItem.classList.contains("has-megamenu")) {
          this._closeMegamenu();
        } else {
          this._closeDropdown(activeItem);
        }
      }
    }, 10);
  }
  _handleMouseOutDelegated(e) {
    if (e.relatedTarget && e.relatedTarget.closest(".pswp")) return;
    if (e.relatedTarget && !this._searchRoot.contains(e.relatedTarget) && (!this._megamenuContainer || !this._megamenuContainer.contains(e.relatedTarget))) {
      if (this._hoverTimeout) clearTimeout(this._hoverTimeout);
      if (this._megamenuHoverTimeout) clearTimeout(this._megamenuHoverTimeout);
      this._hoverTimeoutTarget = "ALL";
      this._hoverTimeout = setTimeout(() => {
        this._closeAllDropdowns();
        this._hoverTimeoutTarget = null;
      }, this.config.hoverDelay);
      return;
    }
    let current = e.target.closest(".menu-header-v3-item.has-dropdown, .menu-header-v3-subitem.has-dropdown");
    let highestLeft = null;
    while (current) {
      if (!current.contains(e.relatedTarget)) {
        highestLeft = current;
        current = current.parentElement.closest(".menu-header-v3-item.has-dropdown, .menu-header-v3-subitem.has-dropdown");
      } else {
        break;
      }
    }
    if (highestLeft) {
      if (highestLeft.classList.contains("has-megamenu")) {
        this._handleMegamenuMouseLeave(highestLeft, e);
      } else {
        this._handleMouseLeave(highestLeft, e);
      }
    }
  }
  _handleContainerMouseOver() {
    if (this._megamenuHoverTimeout) {
      clearTimeout(this._megamenuHoverTimeout);
      this._megamenuHoverTimeout = null;
    }
  }
  _handleContainerMouseOut(e) {
    if (e.relatedTarget && e.relatedTarget.closest(".pswp")) return;
    if (this._megamenuContainer && !this._megamenuContainer.contains(e.relatedTarget)) {
      this._megamenuHoverTimeout = setTimeout(() => {
        this._closeMegamenu();
      }, this.config.hoverDelay);
    }
  }
  _handleClickDelegated(e) {
    const trigger = e.target.closest("[data-menu-trigger]");
    if (trigger) {
      const item = trigger.closest(".menu-header-v3-item.has-dropdown, .menu-header-v3-subitem.has-dropdown");
      if (item) {
        e.preventDefault();
        e.stopPropagation();
        if (item.classList.contains("has-megamenu")) {
          this._toggleMegamenu(item);
        } else {
          this._toggleDropdown(item);
        }
      }
    }
  }
  // ─── Megamenu Open/Close ─────────────────────────────────────────────────────
  _openMegamenu(item) {
    const targetId = item.dataset.megaTarget;
    if (!targetId || !this._megamenuContainer) return;
    if (this._megamenuHoverTimeout) {
      clearTimeout(this._megamenuHoverTimeout);
      this._megamenuHoverTimeout = null;
    }
    this._closeAllDropdowns();
    const megamenu = this._megamenuContainer.querySelector(`[data-mega-id="${targetId}"]`);
    if (!megamenu) return;
    if (this._activeMegamenu && this._activeMegamenu !== megamenu) {
      this._activeMegamenu.classList.remove("active");
      const activeItems = this._searchRoot.querySelectorAll(".menu-header-v3-item.has-megamenu.is-megamenu-active");
      activeItems.forEach((mi) => mi.classList.remove("is-megamenu-active"));
    }
    megamenu.classList.add("active");
    megamenu.inert = false;
    this._activeMegamenu = megamenu;
    this._updateScrollState(megamenu);
    item.classList.add("is-megamenu-active");
    const trigger = item.querySelector("[data-menu-trigger]");
    if (trigger) trigger.setAttribute("aria-expanded", "true");
    if (this._isMobile()) {
      const mobileContainer = this._getMegamenuMobileContainer(targetId);
      if (mobileContainer) {
        mobileContainer.classList.add("is-mobile-active", "is-megamenu-active");
        this._mobileNavLevel = 1;
      }
    }
    this.dispatchEvent(new CustomEvent("dropdown:open", {
      detail: { item, isMegamenu: true },
      bubbles: true
    }));
  }
  _closeMegamenu() {
    if (this._activeMegamenu) {
      const megamenu = this._activeMegamenu;
      this._updateScrollState(megamenu, true);
      megamenu.classList.remove("active");
      megamenu.inert = true;
      this._activeMegamenu = null;
    }
    const activeItems = this._searchRoot.querySelectorAll(".menu-header-v3-item.has-megamenu.is-megamenu-active");
    activeItems.forEach((item) => {
      item.classList.remove("is-megamenu-active");
      const trigger = item.querySelector("[data-menu-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
    });
    if (this._isMobile()) {
      const containers = this._searchRoot.querySelectorAll(".megamenu-mobile-container.is-megamenu-active");
      containers.forEach((el) => el.classList.remove("is-megamenu-active"));
    }
    this.dispatchEvent(new CustomEvent("dropdown:close", {
      detail: { isMegamenu: true },
      bubbles: true
    }));
  }
  _toggleMegamenu(item) {
    if (item.classList.contains("is-megamenu-active")) {
      this._closeMegamenu();
    } else {
      this._openMegamenu(item);
    }
  }
  _handleMegamenuMouseLeave(item, event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && relatedTarget.closest(".pswp")) return;
    if (relatedTarget && this._megamenuContainer && this._megamenuContainer.contains(relatedTarget)) return;
    if (relatedTarget && relatedTarget.closest(".menu-header-v3-item.has-megamenu")) return;
    if (this._megamenuHoverTimeout) clearTimeout(this._megamenuHoverTimeout);
    this._megamenuHoverTimeout = setTimeout(() => {
      this._closeMegamenu();
    }, this.config.hoverDelay);
  }
  // ─── Dropdown Open/Close ─────────────────────────────────────────────────────
  _openDropdown(item) {
    if (item.classList.contains("is-dropdown-active")) return;
    if (this._hoverTimeout) {
      if (this._hoverTimeoutTarget && this._hoverTimeoutTarget !== "ALL" && item.contains(this._hoverTimeoutTarget) && item !== this._hoverTimeoutTarget) ;
      else {
        clearTimeout(this._hoverTimeout);
        this._hoverTimeout = null;
        this._hoverTimeoutTarget = null;
      }
    }
    this._closeMegamenu();
    const parent = item.parentElement;
    const siblings = parent.querySelectorAll(":scope > .menu-header-v3-item.is-dropdown-active, :scope > .menu-header-v3-subitem.is-dropdown-active");
    siblings.forEach((sibling) => {
      if (sibling !== item) this._closeDropdown(sibling);
    });
    item.classList.add("is-dropdown-active");
    this._activeDropdowns.add(item);
    this._updateScrollState(item);
    const trigger = item.querySelector("[data-menu-trigger]");
    if (trigger) trigger.setAttribute("aria-expanded", "true");
    this.dispatchEvent(new CustomEvent("dropdown:open", {
      detail: { item },
      bubbles: true
    }));
  }
  _closeDropdown(item) {
    this._updateScrollState(item, true);
    item.classList.remove("is-dropdown-active");
    this._activeDropdowns.delete(item);
    const nestedActive = item.querySelectorAll(".is-dropdown-active");
    nestedActive.forEach((nested) => {
      nested.classList.remove("is-dropdown-active");
      this._activeDropdowns.delete(nested);
    });
    const mobileActive = item.querySelectorAll(".is-mobile-active");
    mobileActive.forEach((el) => el.classList.remove("is-mobile-active"));
    const trigger = item.querySelector("[data-menu-trigger]");
    if (trigger) trigger.setAttribute("aria-expanded", "false");
    this.dispatchEvent(new CustomEvent("dropdown:close", {
      detail: { item },
      bubbles: true
    }));
  }
  _toggleDropdown(item) {
    if (item.classList.contains("is-dropdown-active")) {
      this._closeDropdown(item);
    } else {
      this._openDropdown(item);
    }
  }
  _handleMouseLeave(item, event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && relatedTarget.closest(".pswp")) return;
    if (relatedTarget && item.contains(relatedTarget)) return;
    if (this._hoverTimeout) clearTimeout(this._hoverTimeout);
    this._hoverTimeoutTarget = item;
    this._hoverTimeout = setTimeout(() => {
      this._closeDropdown(item);
      this._hoverTimeoutTarget = null;
    }, this.config.hoverDelay);
  }
  // ─── Document Click ──────────────────────────────────────────────────────────
  _handleDocumentClick(event) {
    var _a;
    if (this.contains(event.target) || ((_a = this._searchRoot) == null ? void 0 : _a.contains(event.target))) {
      if (!event.target.closest("dropdown-localization-component")) {
        this.closeI18nDropdowns();
      }
      return;
    }
    if (this._megamenuContainer && this._megamenuContainer.contains(event.target)) return;
    if (event.target.closest(".pswp")) return;
    this._closeAllDropdowns();
    this.closeI18nDropdowns();
  }
  /** Close all open i18n dropdowns (click-outside doesn't reach them when the
   *  click lands inside the header/menu tree). */
  closeI18nDropdowns() {
    document.querySelectorAll("dropdown-localization-component").forEach((comp) => {
      var _a;
      (_a = comp.closeDropdown) == null ? void 0 : _a.call(comp);
    });
  }
  // ─── Focus Out (Tab away from header closes menus) ───────────────────────────
  _handleFocusOut() {
    setTimeout(() => {
      const active = document.activeElement;
      if (!active) return;
      const inHeader = this._searchRoot.contains(active);
      const inMegamenu = this._megamenuContainer && this._megamenuContainer.contains(active);
      if (!inHeader && !inMegamenu) {
        this._closeAllDropdowns();
      }
    }, 10);
  }
  // ─── Keyboard Navigation ─────────────────────────────────────────────────────
  _handleKeydown(event) {
    const inHeader = this._searchRoot.contains(event.target);
    const inMegamenu = this._megamenuContainer && this._megamenuContainer.contains(event.target);
    if (!inHeader && !inMegamenu) return;
    if (this._isMobile()) return;
    const { key } = event;
    if (key === "Escape") {
      event.preventDefault();
      this._handleEscapeKey(event.target);
      return;
    }
    const dropdownItem = event.target.closest(".menu-header-v3-subitem");
    const topLevelItem = event.target.closest(".menu-header-v3-item");
    if (topLevelItem && !dropdownItem) {
      this._handleTopLevelKeydown(event, key, topLevelItem);
    } else if (dropdownItem) {
      this._handleDropdownKeydown(event, key, dropdownItem);
    } else if (inMegamenu) {
      this._handleMegamenuKeydown(event, key);
    }
  }
  /**
   * Handle Escape key: close the innermost open context and return focus to trigger
   */
  _handleEscapeKey(target) {
    if (this._megamenuContainer && this._megamenuContainer.contains(target)) {
      const activeTrigger = this._searchRoot.querySelector(".menu-header-v3-item.is-megamenu-active [data-menu-trigger]");
      this._closeMegamenu();
      if (activeTrigger) activeTrigger.focus();
      return;
    }
    const lvl3 = target.closest('.menu-header-v3-dropdown[data-submenu-lvl="2"]');
    if (lvl3) {
      const parentItem = lvl3.closest(".menu-header-v3-subitem.has-dropdown");
      if (parentItem) {
        this._closeDropdown(parentItem);
        const parentTrigger = parentItem.querySelector("[data-menu-trigger]");
        if (parentTrigger) parentTrigger.focus();
      }
      return;
    }
    const lvl2 = target.closest('.menu-header-v3-dropdown[data-submenu-lvl="1"]');
    if (lvl2) {
      const parentItem = lvl2.closest(".menu-header-v3-item.has-dropdown");
      if (parentItem) {
        this._closeDropdown(parentItem);
        const parentTrigger = parentItem.querySelector("[data-menu-trigger]");
        if (parentTrigger) parentTrigger.focus();
      }
      return;
    }
    this._closeAllDropdowns();
  }
  /**
   * Handle arrow keys on top-level menu bar items
   */
  _handleTopLevelKeydown(event, key, currentItem) {
    switch (key) {
      case "ArrowRight": {
        event.preventDefault();
        this._focusNextTopLevelItem(currentItem);
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        this._focusPrevTopLevelItem(currentItem);
        break;
      }
      case "ArrowDown": {
        event.preventDefault();
        if (currentItem.classList.contains("has-dropdown")) {
          this._openTopLevelItem(currentItem);
        }
        break;
      }
      case "Enter":
      case " ": {
        if (currentItem.classList.contains("has-dropdown") && this.config.triggerType === "hover") {
          event.preventDefault();
          if (currentItem.classList.contains("is-dropdown-active")) {
            this._closeDropdown(currentItem);
          } else {
            this._openTopLevelItem(currentItem);
          }
        } else if (currentItem.classList.contains("has-dropdown") && this.config.triggerType === "click") {
          event.preventDefault();
          if (!currentItem.classList.contains("is-dropdown-active")) {
            this._openTopLevelItem(currentItem);
          }
        }
        break;
      }
    }
  }
  /**
   * Handle arrow keys inside a dropdown
   */
  _handleDropdownKeydown(event, key, currentDropdownItem) {
    const dropdownList = currentDropdownItem.closest(".menu-header-v3-sublist");
    if (!dropdownList) return;
    const items = [...dropdownList.querySelectorAll(":scope > .menu-header-v3-subitem")];
    const idx = items.indexOf(currentDropdownItem);
    switch (key) {
      case "ArrowDown": {
        event.preventDefault();
        const next = items[(idx + 1) % items.length];
        const nextLink = next == null ? void 0 : next.querySelector(".menu-header-v3-subitem-link");
        if (nextLink) nextLink.focus();
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        const prev = items[(idx - 1 + items.length) % items.length];
        const prevLink = prev == null ? void 0 : prev.querySelector(".menu-header-v3-subitem-link");
        if (prevLink) prevLink.focus();
        break;
      }
      case "ArrowRight": {
        if (currentDropdownItem.classList.contains("has-dropdown")) {
          event.preventDefault();
          this._openDropdown(currentDropdownItem);
          const subDropdown = currentDropdownItem.querySelector('.menu-header-v3-dropdown[data-submenu-lvl="2"]');
          this._focusFirstInDropdown(subDropdown);
        }
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        const isLvl3 = currentDropdownItem.closest('.menu-header-v3-dropdown[data-submenu-lvl="2"]');
        if (isLvl3) {
          const parentItem = isLvl3.closest(".menu-header-v3-subitem.has-dropdown");
          if (parentItem) {
            this._closeDropdown(parentItem);
            const parentTrigger = parentItem.querySelector("[data-menu-trigger]");
            if (parentTrigger) parentTrigger.focus();
          }
        } else {
          const parentLi = currentDropdownItem.closest(".menu-header-v3-item.has-dropdown");
          if (parentLi) {
            this._closeDropdown(parentLi);
            this._focusPrevTopLevelItem(parentLi);
          }
        }
        break;
      }
    }
  }
  /**
   * Handle arrow keys inside a megamenu panel
   */
  _handleMegamenuKeydown(event, key) {
    if (!this._activeMegamenu) return;
    const focusables = [...this._activeMegamenu.querySelectorAll('a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex="0"]:not([disabled])')];
    const current = focusables.indexOf(document.activeElement);
    switch (key) {
      case "Tab": {
        if (event.shiftKey) {
          if (current <= 0) this._exitMegamenu();
        } else {
          if (current >= focusables.length - 1) this._exitMegamenu();
        }
        break;
      }
      case "ArrowDown": {
        event.preventDefault();
        const next = focusables[(current + 1) % focusables.length];
        if (next) next.focus();
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        const prev = focusables[(current - 1 + focusables.length) % focusables.length];
        if (prev) prev.focus();
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        this._exitMegamenu("prev");
        break;
      }
      case "ArrowRight": {
        event.preventDefault();
        this._exitMegamenu("next");
        break;
      }
    }
  }
  // ─── Close All ───────────────────────────────────────────────────────────────
  _closeAllDropdowns() {
    this._activeDropdowns.forEach((item) => this._closeDropdown(item));
    this._activeDropdowns.clear();
    this._closeMegamenu();
    this.closeI18nDropdowns();
    if (this._isMobile()) {
      this._resetMobileNavigation();
      this._applyInertToDrawerPanels();
    }
  }
  // ─── Mobile Viewport Check ───────────────────────────────────────────────────
  _isMobile() {
    if (this._wasMobile !== null && this._wasMobile !== void 0) return this._wasMobile;
    return window.innerWidth <= this.config.mobileBreakpoint;
  }
  // ─── Focus Helpers ───────────────────────────────────────────────────────────
  /**
   * Close the active megamenu, focus the trigger, and optionally move
   * focus to the previous or next top-level item.
   * Used by Tab and ArrowLeft/ArrowRight when inside a megamenu.
   * @param {'prev'|'next'} [direction] - wrap focus to prev/next item; omit to just close
   */
  _exitMegamenu(direction) {
    var _a;
    const trigger = (_a = this._searchRoot) == null ? void 0 : _a.querySelector(".menu-header-v3-item.is-megamenu-active [data-menu-trigger]");
    this._closeMegamenu();
    if (trigger) {
      trigger.focus();
      if (direction) {
        const parentLi = trigger.closest(".menu-header-v3-item");
        if (parentLi) {
          if (direction === "prev") this._focusPrevTopLevelItem(parentLi);
          else this._focusNextTopLevelItem(parentLi);
        }
      }
    }
  }
  /**
   * Focus the first focusable element inside a megamenu panel.
   * @param {Element} megamenu - the .megamenu-v2 panel element
   */
  _focusFirstInMegamenu(megamenu) {
    const first = megamenu == null ? void 0 : megamenu.querySelector('a, button, [tabindex="0"]');
    if (first) first.focus();
  }
  /**
   * Focus the first .menu-header-v3-subitem-link inside a dropdown panel.
   * @param {Element} dropdown - the .menu-header-v3-dropdown panel element
   */
  _focusFirstInDropdown(dropdown) {
    const first = dropdown == null ? void 0 : dropdown.querySelector(".menu-header-v3-subitem-link");
    if (first) first.focus();
  }
  /**
   * Focus the previous .menu-header-v3-item-link on the top-level menubar.
   * Used by ArrowLeft to wrap around.
   * @param {Element} currentItem - the item the user is currently on
   */
  _focusPrevTopLevelItem(currentItem) {
    const menuList = currentItem.closest(".menu-header-v3-list");
    if (!menuList) return;
    const items = [...menuList.querySelectorAll(":scope > .menu-header-v3-item")];
    const idx = items.indexOf(currentItem);
    const prev = items[(idx - 1 + items.length) % items.length];
    const prevLink = prev == null ? void 0 : prev.querySelector(".menu-header-v3-item-link");
    if (prevLink) prevLink.focus();
  }
  /**
   * Focus the next .menu-header-v3-item-link on the top-level menubar.
   * Used by ArrowRight to wrap around.
   * @param {Element} currentItem - the item the user is currently on
   */
  _focusNextTopLevelItem(currentItem) {
    const menuList = currentItem.closest(".menu-header-v3-list");
    if (!menuList) return;
    const items = [...menuList.querySelectorAll(":scope > .menu-header-v3-item")];
    const idx = items.indexOf(currentItem);
    const next = items[(idx + 1) % items.length];
    const nextLink = next == null ? void 0 : next.querySelector(".menu-header-v3-item-link");
    if (nextLink) nextLink.focus();
  }
  /**
   * Open a top-level menu item and focus the first link inside.
   * Used by ArrowDown and Enter/Space in the top-level keyboard handler.
   * @param {Element} item - .menu-header-v3-item
   */
  _openTopLevelItem(item) {
    var _a;
    if (item.classList.contains("has-megamenu")) {
      this._openMegamenu(item);
      const targetId = item.dataset.megaTarget;
      const megamenu = (_a = this._megamenuContainer) == null ? void 0 : _a.querySelector(`[data-mega-id="${targetId}"]`);
      this._focusFirstInMegamenu(megamenu);
    } else {
      this._openDropdown(item);
      const dropdown = item.querySelector('.menu-header-v3-dropdown[data-submenu-lvl="1"]');
      this._focusFirstInDropdown(dropdown);
    }
  }
  _getDrawerInner() {
    return this._searchRoot.querySelector(".header-drawer-inner");
  }
  // ─── Mobile Focus Management ─────────────────────────────────────────────────
  _applyInertToDrawerPanels() {
    const drawerInner = this._getDrawerInner();
    if (!drawerInner) return;
    drawerInner.querySelectorAll(".menu-header-v3-dropdown, [data-mega-id]").forEach((el) => {
      const container = el.closest(".megamenu-mobile-container") || el;
      const isActive = container.classList.contains("is-mobile-active") || container.classList.contains("is-megamenu-active");
      el.inert = !isActive;
    });
  }
  /**
   * Focus the appropriate element after a drawer level change.
   * @param {number} fromLevel - The level we navigated from (-1 = opening drawer)
   * @param {number} toLevel   - The new active level
   */
  _focusOnDrawerLevelChange(fromLevel, toLevel) {
    if (!isKeyboardUser()) return;
    const drawerInner = this._getDrawerInner();
    if (!drawerInner) return;
    if (toLevel < fromLevel && fromLevel > 0) {
      const trigger = this._mobileTriggers.get(fromLevel);
      if (trigger) {
        trigger.focus();
        return;
      }
    }
    if (toLevel === 0) {
      const toggle = this._searchRoot.querySelector(".header-toggle");
      const toggleBtn = toggle == null ? void 0 : toggle.querySelector("button");
      if (toggleBtn) toggleBtn.focus();
    } else if (toLevel === 1) {
      const active = drawerInner.querySelector('.menu-header-v3-dropdown[data-submenu-lvl="1"].is-mobile-active');
      if (active) {
        const first = active.querySelector(".menu-header-v3-subitem-link");
        if (first) first.focus();
      }
    } else if (toLevel === 2) {
      const active = drawerInner.querySelector('.menu-header-v3-dropdown[data-submenu-lvl="2"].is-mobile-active');
      const back = active == null ? void 0 : active.querySelector(".menu-nav-v3-breadcrumb-item");
      if (back) {
        back.focus();
      } else if (active) {
        const first = active.querySelector(".menu-header-v3-subitem-link");
        if (first) first.focus();
      }
    }
  }
  _setupMobileNavigation() {
    this._boundHandlers.handleMobileClick = this._handleMobileClick.bind(this);
    this._searchRoot.addEventListener("click", this._boundHandlers.handleMobileClick);
  }
  /**
   * Watch the header root for `.is-open` class changes on the mobile drawer.
   * This keeps scroll state in sync even when the drawer is toggled by
   * the header independently of this component.
   *
   * Mobile scroll guard strategy:
   *   - When .is-open is added to the drawer → set up scroll guard on .header-drawer-inner
   *   - When .is-open is removed              → clean up scroll guard
   *
   * This is separate from desktop megamenu scroll state to avoid cross-contamination.
   */
  _setupMobileDrawerObserver() {
    const headerComponent = this._searchRoot;
    if (!headerComponent) return;
    this._mobileDrawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.attributeName === "class" && this._onMobileDrawerClassChange(headerComponent);
      });
    });
    this._mobileDrawerObserver.observe(headerComponent, { attributes: true, attributeFilter: ["class"] });
    this._boundHandlers.handleMobileDrawerClassChange = this._onMobileDrawerClassChange.bind(this);
  }
  _onMobileDrawerClassChange(headerComponent) {
    const isOpen = headerComponent.classList.contains("is-open");
    const drawer = headerComponent.querySelector(".header-drawer");
    if (isOpen && drawer) {
      this._updateMobileScrollState(this._searchRoot);
    } else {
      this._updateMobileScrollState(this._searchRoot, true);
    }
  }
  /**
   * Handle a mobile nav click action (breadcrumb back, top-level forward, dropdown forward).
   */
  _handleMobileClick(e) {
    if (!this._isMobile()) return;
    this.closeI18nDropdowns();
    const breadcrumbItem = e.target.closest(".menu-nav-v3-breadcrumb-item[data-submenu-lvl]");
    if (breadcrumbItem) {
      e.preventDefault();
      e.stopPropagation();
      if (breadcrumbItem.classList.contains("active")) return;
      const targetLevel = parseInt(breadcrumbItem.dataset.submenuLvl, 10);
      this._navigateToMobileLevel(targetLevel);
      return;
    }
    const topLink = e.target.closest(".menu-header-v3-item-link");
    if (topLink) {
      const menuItem = topLink.closest(".menu-header-v3-item");
      if (!menuItem || !menuItem.classList.contains("has-dropdown")) return;
      e.preventDefault();
      e.stopPropagation();
      this._mobileTriggers.set(1, topLink);
      const allLvl2Active = this._searchRoot.querySelectorAll('.menu-header-v3-dropdown[data-submenu-lvl="1"].is-mobile-active');
      allLvl2Active.forEach((el) => el.classList.remove("is-mobile-active"));
      const allLvl3Active = this._searchRoot.querySelectorAll('.menu-header-v3-dropdown[data-submenu-lvl="2"].is-mobile-active');
      allLvl3Active.forEach((el) => el.classList.remove("is-mobile-active"));
      if (menuItem.classList.contains("has-megamenu")) {
        const mobileContainer = menuItem.querySelector(":scope > .megamenu-mobile-container");
        if (mobileContainer) {
          const drawerInner = this._getDrawerInner();
          if (drawerInner) drawerInner.scrollTop = 0;
          if (topLink) topLink.setAttribute("aria-expanded", "true");
          mobileContainer.classList.add("is-mobile-active", "is-megamenu-active");
          this._updateMobileScrollState(this._searchRoot);
          const fromLevel = this._mobileNavLevel;
          this._mobileNavLevel = 1;
          this._applyInertToDrawerPanels();
          this._focusOnDrawerLevelChange(fromLevel, 1);
        }
        return;
      }
      const dropdown = menuItem.querySelector(":scope > .menu-header-v3-dropdown");
      if (dropdown) {
        const drawerInner = this._getDrawerInner();
        if (drawerInner) drawerInner.scrollTop = 0;
        if (topLink) topLink.setAttribute("aria-expanded", "true");
        dropdown.classList.add("is-mobile-active");
        this._updateMobileScrollState(this._searchRoot);
        const fromLevel = this._mobileNavLevel;
        this._mobileNavLevel = 1;
        this._applyInertToDrawerPanels();
        this._focusOnDrawerLevelChange(fromLevel, 1);
      }
      return;
    }
    const dropdownLink = e.target.closest(".menu-header-v3-subitem-link");
    if (dropdownLink) {
      const menuItem = dropdownLink.closest(".menu-header-v3-subitem");
      if (!menuItem || !menuItem.classList.contains("has-dropdown")) return;
      e.preventDefault();
      e.stopPropagation();
      this._mobileTriggers.set(2, dropdownLink);
      const allLvl3Active = this._searchRoot.querySelectorAll('.menu-header-v3-dropdown[data-submenu-lvl="2"].is-mobile-active');
      allLvl3Active.forEach((el) => el.classList.remove("is-mobile-active"));
      const dropdown = menuItem.querySelector(":scope > .menu-header-v3-dropdown");
      if (dropdown) {
        if (dropdownLink) dropdownLink.setAttribute("aria-expanded", "true");
        dropdown.classList.add("is-mobile-active");
        this._updateMobileScrollState(this._searchRoot);
        const fromLevel = this._mobileNavLevel;
        this._mobileNavLevel = 2;
        this._applyInertToDrawerPanels();
        this._focusOnDrawerLevelChange(fromLevel, 2);
      }
      return;
    }
  }
  /**
   * Navigate the mobile drawer back to a specific level.
   * Level 0 = main menu, Level 1 = lvl-2, Level 2 = lvl-3
   */
  _navigateToMobileLevel(level) {
    const fromLevel = this._mobileNavLevel;
    if (level === fromLevel) return;
    if (level === 0) {
      this._resetMobileNavigation();
    } else {
      const allActive = this._searchRoot.querySelectorAll(
        ".menu-header-v3-dropdown.is-mobile-active, .megamenu-mobile-container.is-mobile-active"
      );
      allActive.forEach((el) => {
        const elLvl = parseInt(
          el.dataset.submenuLvl || (el.classList.contains("megamenu-mobile-container") ? "1" : "0"),
          10
        );
        if (elLvl > level) {
          el.classList.remove("is-mobile-active");
          el.classList.remove("is-megamenu-active");
          const trigger = this._mobileTriggers.get(elLvl);
          if (trigger) trigger.setAttribute("aria-expanded", "false");
        }
      });
      this._mobileNavLevel = level;
      this._updateMobileScrollState(this._searchRoot);
    }
    this._applyInertToDrawerPanels();
    this._focusOnDrawerLevelChange(fromLevel, level);
  }
  /**
   * Navigate to lvl-2 (or lvl-3) and optionally activate a specific item.
   * Used by the Theme Editor to open the correct drawer panel when a block is selected.
   * @param {number} targetLevel  - 1 = lvl-2, 2 = lvl-3
   * @param {number} itemIndex    - index of the item to activate within the root list
   * @param {number} [subIndex]   - optional sub-item index for lvl-3
   */
  _navigateToMobileLevelWithItemIndex(targetLevel, itemIndex, subIndex) {
    this._resetMobileNavigation();
    const rootItems = [...this._searchRoot.querySelectorAll(".menu-header-v3-item.has-dropdown")];
    const targetItem = rootItems[itemIndex];
    if (!targetItem) return;
    if (targetLevel === 1) {
      const dropdown = targetItem.querySelector(":scope > .menu-header-v3-dropdown");
      if (dropdown) {
        dropdown.classList.add("is-mobile-active");
        this._mobileNavLevel = 1;
      }
    } else if (targetLevel === 2) {
      const lvl2 = targetItem.querySelector(":scope > .menu-header-v3-dropdown");
      if (!lvl2) return;
      lvl2.classList.add("is-mobile-active");
      if (subIndex !== void 0) {
        const lvl2Items = [...lvl2.querySelectorAll(".menu-header-v3-subitem.has-dropdown")];
        const targetLvl2Item = lvl2Items[subIndex];
        if (targetLvl2Item) {
          const lvl3 = targetLvl2Item.querySelector(":scope > .menu-header-v3-dropdown");
          if (lvl3) {
            lvl3.classList.add("is-mobile-active");
          }
        }
      }
      this._mobileNavLevel = 2;
    }
    this._applyInertToDrawerPanels();
    this._focusOnDrawerLevelChange(-1, this._mobileNavLevel);
  }
  _resetMobileNavigation() {
    const allActive = this._searchRoot.querySelectorAll(".menu-header-v3-dropdown.is-mobile-active");
    allActive.forEach((el) => el.classList.remove("is-mobile-active"));
    const allMegaActive = this._searchRoot.querySelectorAll(".megamenu-mobile-container.is-megamenu-active");
    allMegaActive.forEach((el) => el.classList.remove("is-megamenu-active", "is-mobile-active"));
    this._mobileTriggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });
    this._mobileTriggers.clear();
    this._mobileNavLevel = 0;
    this._updateMobileScrollState(this._searchRoot, true);
  }
  // ─── Megamenu DOM Teleportation ──────────────────────────────────────────────
  /**
   * Move megamenu DOM nodes from .header-mega-container into inline mobile slots.
   * Uses rAF so resize tracking doesn't trigger forced synchronous layouts on low-end devices.
   */
  _teleportMegamenus() {
    if (!this._megamenuContainer) return;
    const slots = this._searchRoot.querySelectorAll(".megamenu-mobile-slot[data-mega-slot]");
    if (!slots.length) return;
    const moves = [];
    slots.forEach((slot) => {
      const handle = slot.dataset.megaSlot;
      const megamenu = this._megamenuContainer.querySelector(`[data-mega-id="${handle}"]`);
      if (megamenu && !slot.contains(megamenu)) {
        moves.push({ slot, megamenu, handle });
      }
    });
    if (!moves.length) return;
    requestAnimationFrame(() => {
      moves.forEach(({ slot, megamenu, handle }) => {
        slot.appendChild(megamenu);
        this._teleportedMegamenus.push({ megamenu, handle });
      });
    });
  }
  /**
   * Move megamenu DOM nodes back to .header-mega-container.
   * Uses rAF for the same performance reason.
   */
  _restoreMegamenus(sync = false) {
    if (!this._megamenuContainer || !this._teleportedMegamenus.length) return;
    const moves = this._teleportedMegamenus.map(({ megamenu }) => ({
      megamenu,
      isInMobileSlot: megamenu.parentElement !== this._megamenuContainer
    }));
    const executeMoves = () => {
      moves.forEach(({ megamenu, isInMobileSlot }) => {
        if (isInMobileSlot) {
          this._megamenuContainer.appendChild(megamenu);
        }
      });
      this._teleportedMegamenus = [];
    };
    if (sync) {
      executeMoves();
    } else {
      requestAnimationFrame(executeMoves);
    }
  }
  // ─── Scroll State (Lenis) ────────────────────────────────────────────────────
  /**
   * Check if an element actually needs a scroll container (content overflows).
   * Uses a 2px buffer to avoid false positives from sub-pixel rounding.
   */
  _needsScroll(el) {
    return el.scrollHeight - el.clientHeight > 2;
  }
  /**
   * Apply or remove data-lenis-prevent attributes on the target container.
   * Only adds attributes when content actually overflows — otherwise leaves
   * attributes off so Lenis isn't accidentally blocked on short content.
   *
   * Also observes the container for dynamic content changes (e.g., accordion
   * panels, loaded products) and re-evaluates the scroll state accordingly.
   */
  _updateScrollState(container, isClosing = false) {
    if (!container) return;
    if (isClosing) {
      this._clearLenisAttrs(container);
      if (this._scrollObserver) {
        this._scrollObserver.disconnect();
        this._scrollObserver = null;
      }
      return;
    }
    if (document.body.dataset.ursusSmoothscroll !== "true") return;
    const desktopSelectors = ".custom-scroll, .megamenu-v2-content";
    container.querySelectorAll(desktopSelectors).forEach((el) => {
      const needs = this._needsScroll(el);
      if (needs) {
        el.setAttribute("data-lenis-prevent", "");
      } else {
        el.removeAttribute("data-lenis-prevent");
      }
    });
    this._observeDynamicContent(container);
  }
  /**
   * Handle scroll guard for the mobile drawer and its panel stack.
   *
   * Mobile layout (≤991px):
   *   .header-drawer-inner      — outer scroller, fills the drawer
   *     .menu-header-v3-dropdown — absolute panels stacked on top of each other
   *
   * The drawer itself is locked by header-v3 when open (lenis.stop / overflow:hidden).
   * When a panel inside is taller than the drawer, user scroll should be contained
   * in .header-drawer-inner so the breadcrumb and panel content can scroll
   * independently from the outer wrapper.
   *
   * Only one observer is active at a time — switching panels cleans up the previous.
   */
  _updateMobileScrollState(drawer, isClosing = false) {
    if (!drawer) return;
    if (isClosing) {
      if (this._scrollObserver) {
        this._scrollObserver.disconnect();
        this._scrollObserver = null;
      }
      if (this._scrollDebounce) {
        clearTimeout(this._scrollDebounce);
        this._scrollDebounce = null;
      }
      this._activeMobileDrawer = null;
      this._activeMobilePanels.clear();
      return;
    }
    if (document.body.dataset.ursusSmoothscroll !== "true") return;
    this._activeMobileDrawer = drawer;
    const drawerEl = drawer.querySelector(".header-drawer-inner");
    if (drawerEl && this._needsScroll(drawerEl)) {
      drawerEl.setAttribute("data-lenis-prevent", "");
    } else if (drawerEl) {
      drawerEl.removeAttribute("data-lenis-prevent");
    }
    drawer.querySelectorAll(
      ".menu-header-v3-dropdown.is-mobile-active, .megamenu-mobile-container.is-megamenu-active"
    ).forEach((panel) => {
      if (this._needsScroll(panel)) {
        panel.setAttribute("data-lenis-prevent", "");
      } else {
        panel.removeAttribute("data-lenis-prevent");
      }
    });
    const openPanels = drawer.querySelectorAll(
      ".menu-header-v3-dropdown.is-mobile-active, .megamenu-mobile-container.is-megamenu-active"
    );
    this._activeMobilePanels = new Set(openPanels);
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
    }
    if (this._scrollDebounce) clearTimeout(this._scrollDebounce);
    this._scrollObserver = new MutationObserver(() => {
      if (this._scrollDebounce) clearTimeout(this._scrollDebounce);
      this._scrollDebounce = setTimeout(() => {
        this._reapplyMobileDrawerState();
      }, 100);
    });
    this._scrollObserver.observe(drawer, { childList: true, subtree: true });
  }
  /**
   * Re-check overflow after dynamic content mutations in the mobile drawer.
   */
  _reapplyMobileDrawerState() {
    if (!this._activeMobileDrawer) return;
    if (document.body.dataset.ursusSmoothscroll !== "true") return;
    const drawerEl = this._activeMobileDrawer.querySelector(".header-drawer-inner");
    if (!drawerEl) return;
    if (this._needsScroll(drawerEl)) {
      drawerEl.setAttribute("data-lenis-prevent", "");
    } else {
      drawerEl.removeAttribute("data-lenis-prevent");
    }
    this._activeMobileDrawer.querySelectorAll(
      ".menu-header-v3-dropdown.is-mobile-active, .megamenu-mobile-container.is-megamenu-active"
    ).forEach((panel) => {
      if (this._needsScroll(panel)) {
        panel.setAttribute("data-lenis-prevent", "");
      } else {
        panel.removeAttribute("data-lenis-prevent");
      }
    });
  }
  /**
   * Watch for mutations that may change the inner height of scrollable elements.
   * On any added/removed node, re-evaluate all scroll targets within the container.
   * Uses a small debounce so rapid mutations (e.g. many elements loading) don't
   * trigger excessive re-checks.
   */
  _observeDynamicContent(container) {
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
    }
    if (this._scrollDebounce) clearTimeout(this._scrollDebounce);
    this._scrollObserver = new MutationObserver(() => {
      if (this._scrollDebounce) clearTimeout(this._scrollDebounce);
      this._scrollDebounce = setTimeout(() => {
        container.querySelectorAll(".custom-scroll, .megamenu-v2-content").forEach((el) => {
          const needs = this._needsScroll(el);
          if (needs) {
            el.setAttribute("data-lenis-prevent", "");
          } else {
            el.removeAttribute("data-lenis-prevent");
          }
        });
      }, 100);
    });
    this._scrollObserver.observe(container, {
      childList: true,
      subtree: true
    });
  }
  /**
   * Remove all Lenis scroll-guard attributes from a container and its children.
   */
  _clearLenisAttrs(container) {
    container.querySelectorAll("[data-lenis-prevent]").forEach((el) => {
      el.removeAttribute("data-lenis-prevent");
    });
    if (container.hasAttribute("data-lenis-prevent")) {
      container.removeAttribute("data-lenis-prevent");
    }
  }
  // ─── Resize Handler ──────────────────────────────────────────────────────────
  _setupResizeHandler() {
    this._wasMobile = window.innerWidth <= this.config.mobileBreakpoint;
    this._boundResizeHandler = this._handleResize.bind(this);
    window.addEventListener("resize", this._boundResizeHandler);
  }
  _handleResize() {
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    this._resizeTimeout = setTimeout(() => {
      this._checkBreakpointChange();
      if (this._activeMegamenu) this._updateScrollState(this._activeMegamenu);
      this._activeDropdowns.forEach((dropdown) => this._updateScrollState(dropdown));
    }, 150);
  }
  _checkBreakpointChange() {
    const wasMobile = this._wasMobile;
    const isMobile = window.innerWidth <= this.config.mobileBreakpoint;
    if (wasMobile === isMobile) return;
    this._wasMobile = isMobile;
    this._updateMobileScrollState(this._searchRoot, true);
    this._updateDesktopDelegation();
    this._closeAllDropdowns();
    if (isMobile) {
      this._teleportMegamenus();
    } else {
      this._restoreMegamenus();
      const header = this.closest("header-v3-component");
      if (header == null ? void 0 : header._isNavOpen) header._toggleNav();
    }
  }
  // ─── Cleanup ─────────────────────────────────────────────────────────────────
  disconnectedCallback() {
    var _a, _b;
    document.removeEventListener("click", this._boundHandlers.handleDocumentClick);
    document.removeEventListener("keydown", this._boundHandlers.handleKeydown);
    (_a = this._searchRoot) == null ? void 0 : _a.removeEventListener("focusout", this._boundHandlers.handleFocusOut);
    if (this._boundHandlers.handleMobileClick) {
      (_b = this._searchRoot) == null ? void 0 : _b.removeEventListener("click", this._boundHandlers.handleMobileClick);
    }
    this.removeEventListener("close-all-dropdowns", this._boundHandlers.handleCloseAll);
    if (this._desktopEventsBound) {
      if (this._searchRoot) {
        this._searchRoot.removeEventListener("mouseover", this._boundHandlers.handleMouseOver);
        this._searchRoot.removeEventListener("mouseout", this._boundHandlers.handleMouseOut);
        this._searchRoot.removeEventListener("click", this._boundHandlers.handleClick);
        this._searchRoot.removeEventListener("focusin", this._boundHandlers.handleFocusIn);
        this._searchRoot.removeEventListener("focusout", this._boundHandlers.handleFocusOut);
        this._searchRoot.removeEventListener("focusout", this._boundHandlers.handleFocusOutDelegated);
      }
      if (this._megamenuContainer) {
        this._megamenuContainer.removeEventListener("mouseover", this._boundHandlers.handleContainerMouseOver);
        this._megamenuContainer.removeEventListener("mouseout", this._boundHandlers.handleContainerMouseOut);
      }
    }
    if (this._boundResizeHandler) {
      window.removeEventListener("resize", this._boundResizeHandler);
    }
    if (this._hoverTimeout) clearTimeout(this._hoverTimeout);
    if (this._megamenuHoverTimeout) {
      clearTimeout(this._megamenuHoverTimeout);
      this._megamenuHoverTimeout = null;
    }
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = null;
    }
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
      this._scrollObserver = null;
    }
    if (this._scrollDebounce) {
      clearTimeout(this._scrollDebounce);
      this._scrollDebounce = null;
    }
    if (this._mobileDrawerObserver) {
      this._mobileDrawerObserver.disconnect();
      this._mobileDrawerObserver = null;
    }
    this._restoreMegamenus(true);
  }
}
if (!customElements.get("menu-header-v2")) {
  customElements.define("menu-header-v2", HeaderMenuV2);
}
